import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useNavigate, Routes, Route } from "react-router";
import { App, RequirePolicy } from "@livefree/applications";
import { ReactiveCore, replaceRows, DexieDatabaseProvider } from "@livefree/reactive";
import { dexieRequestBrokerAddon } from "@livefree/request-broker";
import { RestClient } from "@livefree/js-sdk";
import { EnvironmentConfigurations, useRuntimeProvider, alerts } from "@livefree/runtime";
import { ListBox, Button, Card, Spinner, Breadcrumbs, BreadcrumbsItem } from "@heroui/react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAgenda, Agenda } from "@heroui-pro/react";
import { FlatCard, withDrawerController, useDrawerController, Forbidden } from "@livefree/react-ui";
import { Uniformly, TextAreaField, DateTimeField, USTimeZoneComboBoxField, CheckboxField, EmailField, PhoneField, SelectField } from "@livefree/uniformly";
import { parseDateTime } from "@internationalized/date";
import { toISO, DateTime } from "@livefree/common";
const reactive = new ReactiveCore("reminders");
const ReminderChannels = {
  None: 0,
  Sms: 1,
  Email: 2,
  /** A Teams direct message from the Workflows bot, to `teamsAddress`. */
  Teams: 4,
  /** A post to a shared Teams channel, the Channel destination named by `teamsChannelKey`. */
  TeamsChannel: 8
};
const hasChannel = (channels, channel) => (channels & channel) === channel && channel !== ReminderChannels.None;
class RemindersClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "reminders");
  }
  /** `POST /reminders` — omit `accountId` to create against the authenticated user's own account. */
  async createReminder(request) {
    return await this.create(request);
  }
  /** `GET /reminders/{id}`. */
  async getReminder(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}`) });
  }
  /**
   * `PUT /reminders/{id}` — including the time. The scheduling module supports moving a one-time
   * schedule's instant, so editing a reminder's time is an update rather than a delete-and-recreate.
   */
  async updateReminder(id, request) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}`),
      body: request
    });
  }
  /** `DELETE /reminders/{id}` — also cancels the underlying schedule. Returns 204 No Content. */
  async deleteReminder(id) {
    await this.httpDELETE({ url: this.appendUrl(`/${id}`) });
  }
  /** `GET /reminders/account/{accountId}` — reminders for a specific account. */
  async getForAccount(accountId) {
    return await this.httpGET({
      url: this.appendUrl(`/account/${accountId}`)
    });
  }
  /**
   * `GET /reminders/mine` — reminders for the authenticated user's own account, resolved server-side
   * from `dbo.Accounts.ExternalId`. This is what lets the calendar work without being told an account.
   */
  async getMine() {
    return await this.httpGET({ url: this.appendUrl("/mine") });
  }
  /**
   * `GET /reminders/mine/by-source` — the authenticated user's reminder for one item, or `null` when
   * they have none. The API answers a JSON `null` (200) rather than a 404, so "no reminder yet" is a
   * value here, not a rejection.
   */
  async findMineBySource(source, sourceKey) {
    return await this.httpGET({
      url: this.appendUrl("/mine/by-source"),
      query: { source, sourceKey }
    }) ?? null;
  }
}
const CHANNEL_KIND = 1;
class TeamsChannelsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "notifications");
  }
  /** `GET /notifications/teams-destinations`, limited to Channel destinations and sorted by name. */
  async getChannels() {
    const destinations = await this.httpGET({
      url: this.appendUrl("/teams-destinations")
    }) ?? [];
    return destinations.filter((destination) => destination.kind === CHANNEL_KIND).map(({ id, name, description, uniqueKey }) => ({ id, name, description, uniqueKey })).sort((a, b) => a.name.localeCompare(b.name));
  }
}
const RemindersConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "RemindersAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "RemindersAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "RemindersAPI"
    }
  }
});
const RemindersService = reactive.service(
  "reminders",
  RemindersClient
)((Service) => new Service(RemindersConfig.current.api.host));
RemindersService.reactor("remindersReactor", {
  success: (response, _, db2) => {
    const { source, payload, args } = response;
    switch (source) {
      // Both list calls own the whole local set: the calendar shows one account at a time, so a
      // switch between "mine" and a specific account should not leave the previous rows behind.
      case RemindersService.getMine:
      case RemindersService.getForAccount:
        return replaceRows(db2, db2.reminders, payload);
      case RemindersService.getReminder:
      case RemindersService.createReminder:
      case RemindersService.updateReminder:
        return db2.reminders.put(payload);
      case RemindersService.deleteReminder: {
        const [id] = args;
        return db2.reminders.delete(id);
      }
    }
  },
  error: (response) => appRuntime.services.alerts.error(response.payload?.message ?? "Error")
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const db = new DexieDatabaseProvider([dexieRequestBrokerAddon]);
const ReactiveConfig = {
  db: {
    name: "livefree.reminders.db",
    provider: db,
    init: (db2) => {
      db2.version(1).stores(storeFormat(_store));
    }
  }
};
const _store = {
  // Reminders indexed by accountId so the calendar can scope a query to whichever account it is
  // showing, and by runOnUtc so a month's worth can be read in date order.
  reminders: ["&id", "accountId", "runOnUtc"]
};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped$1;
const AppLayout = render(({ children }) => {
  Bootstrapped$1 ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped$1, { children });
});
const RoutePaths = () => {
  const baseUrl = appRuntime.baseUrl;
  const rootPaths = {
    reminders: baseUrl + "/reminders"
  };
  return {
    reminders: {
      list: () => rootPaths.reminders,
      create: () => `${rootPaths.reminders}/create`,
      details: (id) => `${rootPaths.reminders}/${id}/details`
    }
  };
};
const pad = (n) => String(n).padStart(2, "0");
const dateKeyOf = (runOnLocal) => (runOnLocal ?? "").slice(0, 10);
const dateKeyOfCalendarDate = (date) => `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
const timeOf = (runOnLocal) => (runOnLocal ?? "").slice(11, 16) || "09:00";
const formatTime = (runOnLocal) => {
  const [hours, minutes] = timeOf(runOnLocal).split(":").map(Number);
  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${suffix}`;
};
const formatShortDateTime = (runOnLocal) => {
  const key = dateKeyOf(runOnLocal);
  if (!key) return "";
  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(year, month - 1, day).toLocaleDateString(void 0, {
    month: "short",
    day: "numeric",
    ...year === (/* @__PURE__ */ new Date()).getFullYear() ? {} : { year: "numeric" }
  });
  return `${date}, ${formatTime(runOnLocal)}`;
};
const formatDateKey = (dateKey) => {
  if (!dateKey) return "";
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(void 0, {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};
const localWallClockToUtcIso = (local) => local ? toISO(local) : "";
const utcIsoToLocalWallClock = (iso) => iso ? DateTime.fromISO(iso, { zone: "utc" }).toLocal().toFormat("yyyy-MM-dd'T'HH:mm:ss") : "";
const nextHourLocal = () => DateTime.now().plus({ hours: 1 }).startOf("hour").toFormat("yyyy-MM-dd'T'HH:mm:ss");
const agendaStartToLocal = (start) => start ? `${dateKeyOfCalendarDate(start)}T${pad(start.hour)}:${pad(start.minute)}:00` : "";
const toAgendaEvents = (reminders) => (reminders ?? []).filter((reminder) => reminder.runOnLocal).map((reminder) => {
  const start = parseDateTime(reminder.runOnLocal);
  return {
    id: String(reminder.id),
    title: reminder.text,
    start,
    end: start.add({ minutes: 30 }),
    // Delivered reminders can't be edited, so make them undraggable too.
    isReadOnly: Boolean(reminder.sentOnUtc),
    // Dashed until it has actually gone out.
    status: reminder.sentOnUtc ? "confirmed" : "unconfirmed"
  };
});
let client$1 = null;
const getClient$1 = () => client$1 ??= new TeamsChannelsClient(RemindersConfig.current.api.host);
const useTeamsChannels = (enabled) => {
  const [state, setState] = useState({ channels: [], isLoading: false, error: null });
  useEffect(() => {
    if (!enabled) return;
    let isCurrent = true;
    setState((previous) => ({ ...previous, isLoading: true, error: null }));
    getClient$1().getChannels().then((channels) => isCurrent && setState({ channels, isLoading: false, error: null })).catch((error) => isCurrent && setState({ channels: [], isLoading: false, error }));
    return () => {
      isCurrent = false;
    };
  }, [enabled]);
  return state;
};
const prefillPhone = (phone) => {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return phone ?? "";
};
const defaultRunOn = (runOnLocal) => localWallClockToUtcIso(runOnLocal || nextHourLocal());
const ReminderForm = ({
  reminder,
  runOnLocal,
  defaults,
  allowTeamsChannel = false,
  isSaving,
  onSave,
  onCancel,
  onDelete
}) => {
  const [form, setForm] = useState({});
  const { user } = useRuntimeProvider();
  const isSent = Boolean(reminder?.sentOnUtc);
  const initial = {
    text: reminder?.text ?? defaults?.text ?? "",
    runOn: reminder ? localWallClockToUtcIso(reminder.runOnLocal) : defaultRunOn(runOnLocal),
    // A new reminder leaves this empty, and the field starts at the browser's zone.
    timeZoneId: reminder?.timeZoneId ?? null,
    email: reminder ? hasChannel(reminder.channels, ReminderChannels.Email) : true,
    sms: reminder ? hasChannel(reminder.channels, ReminderChannels.Sms) : false,
    teams: reminder ? hasChannel(reminder.channels, ReminderChannels.Teams) : false,
    teamsChannel: reminder ? hasChannel(reminder.channels, ReminderChannels.TeamsChannel) : false,
    emailAddress: reminder?.email ?? user?.email ?? "",
    phone: reminder?.phone ?? prefillPhone(user?.phone),
    teamsAddress: reminder?.teamsAddress ?? user?.email ?? "",
    teamsChannelKey: reminder?.teamsChannelKey ?? null
  };
  const showTeamsChannel = allowTeamsChannel || initial.teamsChannel;
  const data = form.data ?? initial;
  const wantsEmail = Boolean(data.email);
  const wantsSms = Boolean(data.sms);
  const wantsTeams = Boolean(data.teams);
  const wantsTeamsChannel = showTeamsChannel && Boolean(data.teamsChannel);
  const hasChannelSelected = wantsEmail || wantsSms || wantsTeams || wantsTeamsChannel;
  const teamsChannels = useTeamsChannels(wantsTeamsChannel);
  const defaultTeamsChannelKey = initial.teamsChannelKey ?? (teamsChannels.channels.length === 1 ? teamsChannels.channels[0].uniqueKey : null);
  const submit = () => onSave({
    text: (data.text ?? "").trim(),
    channels: (wantsSms ? ReminderChannels.Sms : 0) | (wantsEmail ? ReminderChannels.Email : 0) | (wantsTeams ? ReminderChannels.Teams : 0) | (wantsTeamsChannel ? ReminderChannels.TeamsChannel : 0),
    runOnLocal: utcIsoToLocalWallClock(data.runOn),
    timeZoneId: data.timeZoneId,
    email: wantsEmail ? (data.emailAddress ?? "").trim() : null,
    phone: wantsSms ? (data.phone ?? "").trim() : null,
    teamsAddress: wantsTeams ? (data.teamsAddress ?? "").trim() : null,
    teamsChannelKey: wantsTeamsChannel ? data.teamsChannelKey ?? null : null,
    url: reminder?.url ?? defaults?.url ?? null
  });
  return /* @__PURE__ */ jsxs(FlatCard, { className: "p-1 rounded-none", children: [
    /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsxs(Uniformly, { name: "reminder", className: "flex flex-col gap-4", onChange: setForm, children: [
      /* @__PURE__ */ jsx(
        TextAreaField,
        {
          name: "text",
          label: "Reminder",
          value: initial.text,
          isDisabled: isSent,
          validation: (x) => x.string().required("Enter what the reminder should say.")
        }
      ),
      /* @__PURE__ */ jsx(
        DateTimeField,
        {
          name: "runOn",
          label: "When",
          value: initial.runOn,
          isDisabled: isSent,
          granularity: "minute",
          hourCycle: 12,
          validation: (x) => x.date().required("Choose when to send it.")
        }
      ),
      /* @__PURE__ */ jsx(
        USTimeZoneComboBoxField,
        {
          name: "timeZoneId",
          label: "Time zone",
          value: initial.timeZoneId,
          isDisabled: isSent,
          validation: (x) => x.string().required("Choose a time zone.")
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: "Send via" }),
        /* @__PURE__ */ jsxs("div", { className: "flex gap-4", children: [
          /* @__PURE__ */ jsx(CheckboxField, { name: "email", value: initial.email, isDisabled: isSent, children: "Email" }),
          /* @__PURE__ */ jsx(CheckboxField, { name: "sms", value: initial.sms, isDisabled: isSent, children: "SMS" }),
          /* @__PURE__ */ jsx(CheckboxField, { name: "teams", value: initial.teams, isDisabled: isSent, children: "Teams" }),
          showTeamsChannel && /* @__PURE__ */ jsx(CheckboxField, { name: "teamsChannel", value: initial.teamsChannel, isDisabled: isSent, children: "Teams channel" })
        ] })
      ] }),
      wantsEmail && /* @__PURE__ */ jsx(
        EmailField,
        {
          name: "emailAddress",
          label: "Email address",
          value: initial.emailAddress,
          isDisabled: isSent,
          required: true,
          requiredMessage: "An email address is required."
        }
      ),
      wantsSms && /* @__PURE__ */ jsx(
        PhoneField,
        {
          name: "phone",
          label: "Phone number",
          value: initial.phone,
          isDisabled: isSent,
          validation: (x) => x.string().required("A phone number is required.")
        }
      ),
      wantsTeams && /* @__PURE__ */ jsx(
        EmailField,
        {
          name: "teamsAddress",
          label: "Teams sign-in (Microsoft 365)",
          value: initial.teamsAddress,
          isDisabled: isSent,
          required: true,
          requiredMessage: "A Teams sign-in is required."
        }
      ),
      wantsTeamsChannel && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          SelectField,
          {
            name: "teamsChannelKey",
            label: "Teams channel",
            placeholder: "Choose a channel",
            value: defaultTeamsChannelKey,
            items: teamsChannels.channels.map((channel) => ({
              ...channel,
              id: channel.uniqueKey
            })),
            isLoading: teamsChannels.isLoading,
            isDisabled: isSent,
            validation: (x) => x.string().required("Choose a Teams channel."),
            children: (channel) => /* @__PURE__ */ jsx(ListBox.Item, { id: channel.id, textValue: channel.name, children: channel.name }, channel.id)
          }
        ),
        teamsChannels.error && /* @__PURE__ */ jsx("p", { className: "text-sm text-danger", children: "Could not load the Teams channels. You need permission to view Teams destinations." }),
        !teamsChannels.isLoading && !teamsChannels.error && teamsChannels.channels.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-sm text-warning", children: "No Teams channels are set up yet. Ask an administrator to add one." })
      ] }),
      !hasChannelSelected && /* @__PURE__ */ jsx("p", { className: "text-sm text-danger", children: "Choose at least one delivery channel." }),
      isSent && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted", children: [
        "Sent on ",
        new Date(reminder.sentOnUtc).toLocaleString(),
        " — a delivered reminder cannot be changed."
      ] })
    ] }) }),
    /* @__PURE__ */ jsxs(FlatCard.Footer, { className: "flex flex-row items-center justify-between gap-2", children: [
      reminder && !isSent ? /* @__PURE__ */ jsx(Button, { variant: "tertiary", isDisabled: isSaving, onPress: () => onDelete(reminder), children: "Delete" }) : /* @__PURE__ */ jsx("span", {}),
      /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
        /* @__PURE__ */ jsx(Button, { variant: "secondary", isDisabled: isSaving, onPress: onCancel, children: "Cancel" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "primary",
            isDisabled: isSaving || isSent || !form.isValid || !hasChannelSelected,
            onPress: submit,
            children: reminder ? "Save" : "Create"
          }
        )
      ] })
    ] })
  ] });
};
const ReminderFormDrawer = ({
  item,
  allowTeamsChannel,
  isSaving,
  onSave,
  onDelete,
  onClose
}) => {
  if (!item) return null;
  const reminder = item.reminder ?? null;
  return /* @__PURE__ */ jsx(
    ReminderForm,
    {
      reminder,
      runOnLocal: item.runOnLocal,
      defaults: item.defaults,
      allowTeamsChannel,
      isSaving,
      onSave: async (values) => {
        await onSave(reminder, values);
        onClose();
      },
      onDelete: async (target) => {
        await onDelete(target);
        onClose();
      },
      onCancel: onClose
    }
  );
};
ReminderFormDrawer.Drawer = withDrawerController((props) => /* @__PURE__ */ jsx(ReminderFormDrawer, { ...props }));
const RemindersCalendarView = ({
  reminders,
  isLoading,
  isSaving,
  openCreate = false,
  onSave,
  onDelete
}) => {
  const navigate = useNavigate();
  const events = useMemo(() => toAgendaEvents(reminders ?? []), [reminders]);
  const byId = useMemo(
    () => new Map((reminders ?? []).map((reminder) => [String(reminder.id), reminder])),
    [reminders]
  );
  const [drawer, ReminderDrawer] = useDrawerController(ReminderFormDrawer.Drawer, {
    size: "md",
    // Required: withDrawerController defaults isOpen to true, and the hook forwards undefined
    // unless it's set here — so omitting it renders the drawer open on first paint.
    isOpen: false,
    // Drop back to the list URL when the drawer was opened by the /create route: staying on
    // /create would mean a second "New Reminder" click navigates nowhere, so `openCreate` never
    // changes, the effect below never refires, and the button silently stops working.
    onClose: () => {
      if (openCreate) navigate(RoutePaths().reminders.list());
    }
  });
  useEffect(() => {
    if (openCreate) drawer.open({ runOnLocal: nextHourLocal() });
  }, [openCreate]);
  const agenda = useAgenda({
    events,
    defaultView: "month",
    onEventSelect: (id) => {
      const reminder = id && byId.get(id);
      if (reminder) drawer.open({ reminder });
    },
    onEventCreate: ({ start }) => drawer.open({ runOnLocal: agendaStartToLocal(start) })
  });
  if (isLoading && !reminders?.length) {
    return /* @__PURE__ */ jsx(Card, { className: "flex-1 min-h-0", children: /* @__PURE__ */ jsx(Card.Content, { className: "h-full flex items-center justify-center", children: /* @__PURE__ */ jsx(Spinner, {}) }) });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Card, { className: "flex-1 min-h-0", children: /* @__PURE__ */ jsx(Card.Content, { className: "h-full min-h-0 p-3", children: /* @__PURE__ */ jsxs(Agenda, { ...agenda, className: "h-full", children: [
      /* @__PURE__ */ jsxs(Agenda.Header, { children: [
        /* @__PURE__ */ jsx(Agenda.Heading, {}),
        /* @__PURE__ */ jsx(Agenda.ViewSelector, {}),
        /* @__PURE__ */ jsxs(Agenda.Navigation, { children: [
          /* @__PURE__ */ jsx(Agenda.NavButton, { slot: "previous" }),
          /* @__PURE__ */ jsx(Agenda.TodayButton, {}),
          /* @__PURE__ */ jsx(Agenda.NavButton, { slot: "next" })
        ] })
      ] }),
      /* @__PURE__ */ jsxs(Agenda.Body, { children: [
        /* @__PURE__ */ jsx(Agenda.WeekHeader, {}),
        /* @__PURE__ */ jsxs(Agenda.TimeGrid, { children: [
          /* @__PURE__ */ jsx(Agenda.CurrentTimeIndicator, {}),
          agenda.visibleDays.map((day) => /* @__PURE__ */ jsx(Agenda.DayColumn, { date: day, children: agenda.getEventsForDay(day).map((event) => /* @__PURE__ */ jsx(Agenda.Event, { event }, event.id)) }, day.toString()))
        ] }),
        /* @__PURE__ */ jsx(Agenda.MonthGrid, { children: agenda.visibleWeeks.map((week, index) => {
          const rowLayout = agenda.getMonthRowLayout(week);
          return /* @__PURE__ */ jsx(Agenda.MonthRow, { spanningRowCount: rowLayout.rowCount, children: week.map((day, colIndex) => /* @__PURE__ */ jsx(
            Agenda.MonthCell,
            {
              date: day,
              spanningRowCount: rowLayout.rowCountPerCol[colIndex] ?? 0,
              children: agenda.getPerCellEvents(day, week).map((event) => /* @__PURE__ */ jsx(Agenda.MonthEvent, { event }, event.id))
            },
            day.toString()
          )) }, index);
        }) })
      ] })
    ] }) }) }),
    /* @__PURE__ */ jsx(ReminderDrawer, { isSaving, onSave, onDelete })
  ] });
};
const RemindersCalendarController = reactive.component(
  ({ accountId, openCreate, services, data, monitors }) => {
    const { reminders } = data;
    useEffect(() => {
      if (accountId) services.reminders.getForAccount(accountId);
      else services.reminders.getMine();
    }, [accountId, services]);
    const handleSave = useCallback(
      (existing, values) => existing ? services.reminders.updateReminder(existing.id, values) : services.reminders.createReminder({ ...values, accountId: accountId ?? null }),
      [accountId, services]
    );
    const handleDelete = useCallback(
      (reminder) => services.reminders.deleteReminder(reminder.id),
      [services]
    );
    const isLoading = monitors.getMine.executing || monitors.getForAccount.executing;
    const isSaving = monitors.createReminder.executing || monitors.updateReminder.executing || monitors.deleteReminder.executing;
    return /* @__PURE__ */ jsx(
      RemindersCalendarView,
      {
        reminders,
        isLoading,
        isSaving,
        openCreate,
        onSave: handleSave,
        onDelete: handleDelete
      }
    );
  },
  {
    name: "RemindersCalendarController",
    queries: () => [
      {
        name: "reminders",
        // .toArray() matters: orderBy alone returns a Dexie Collection, which is truthy but not
        // iterable, so it slips past a `?? []` guard and blows up on the first for-of.
        execute: (db2) => db2.reminders.orderBy("runOnUtc").toArray(),
        deps: []
      }
    ],
    monitors: (services) => [
      services.reminders.getMine,
      services.reminders.getForAccount,
      services.reminders.createReminder,
      services.reminders.updateReminder,
      services.reminders.deleteReminder
    ]
  }
);
const RemindersView = ({ openCreate = false }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4 w-full h-full", children: [
  /* @__PURE__ */ jsx("div", { className: "flex justify-between items-center", children: /* @__PURE__ */ jsxs(Breadcrumbs, { children: [
    /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Reminders" }),
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Calendar" })
  ] }) }),
  /* @__PURE__ */ jsx(RemindersCalendarController, { openCreate })
] });
const RemindersRoutes = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(RemindersView, { openCreate: true }) }),
  /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(RemindersView, {}) })
] });
const Passthrough = ({ children }) => children;
let Bootstrapped;
const Embed = render(({ children }) => {
  Bootstrapped ??= App.bootstrap(Passthrough, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
});
const RemindersCalendar = ({ accountId }) => /* @__PURE__ */ jsx(Embed, { children: /* @__PURE__ */ jsx("div", { className: "h-full flex flex-col", children: /* @__PURE__ */ jsx(RemindersCalendarController, { accountId }) }) });
const SOURCE_REMINDER = "reminders.source-reminder";
let client = null;
const getClient = () => client ??= new RemindersClient(RemindersConfig.current.api.host);
const errorMessage = (error, fallback) => error instanceof Error ? error.message : fallback;
const SourceReminderController = ({
  source,
  sourceKey,
  text,
  url,
  defaultRunOnUtc,
  allowTeamsChannel = false
}) => {
  const key = sourceKey === null || sourceKey === void 0 ? "" : String(sourceKey);
  const [reminder, setReminder] = useState(void 0);
  const [isSaving, setIsSaving] = useState(false);
  const latestRequest = useRef(0);
  useEffect(() => {
    if (!key) return;
    const request = ++latestRequest.current;
    setReminder(void 0);
    getClient().findMineBySource(source, key).then((found) => request === latestRequest.current && setReminder(found ?? null)).catch((error) => {
      if (request !== latestRequest.current) return;
      setReminder(null);
      alerts.error(errorMessage(error, "Could not load the reminder."));
    });
  }, [source, key]);
  const [drawer, ReminderDrawer] = useDrawerController(ReminderFormDrawer.Drawer, {
    size: "md",
    // withDrawerController defaults isOpen to true; without this the drawer renders open.
    isOpen: false
  });
  const run = useCallback(async (work, fallback) => {
    setIsSaving(true);
    try {
      return await work();
    } catch (error) {
      alerts.error(errorMessage(error, fallback));
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, []);
  const handleSave = useCallback(
    (existing, values) => run(async () => {
      const saved = existing ? await getClient().updateReminder(existing.id, values) : await getClient().createReminder({ ...values, source, sourceKey: key });
      setReminder(saved);
    }, "Could not save the reminder."),
    [run, source, key]
  );
  const handleDelete = useCallback(
    (target) => run(async () => {
      await getClient().deleteReminder(target.id);
      setReminder(null);
    }, "Could not delete the reminder."),
    [run]
  );
  if (!key) {
    return /* @__PURE__ */ jsxs("p", { className: "flex items-center gap-1.5 text-sm text-warning", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-alarm-line", "aria-hidden": true }),
      "Save first to add a reminder."
    ] });
  }
  const open = () => drawer.open(
    reminder ? { reminder } : {
      runOnLocal: defaultRunOnUtc ? utcIsoToLocalWallClock(defaultRunOnUtc) : void 0,
      defaults: { text, url }
    }
  );
  const label = reminder ? formatShortDateTime(reminder.runOnLocal) : "Remind me";
  const fullLabel = reminder ? `${reminder.sentOnUtc ? "Reminded" : "Reminder set for"} ${formatDateKey(dateKeyOf(reminder.runOnLocal))}, ${formatTime(reminder.runOnLocal)}` : "Remind me";
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      Button,
      {
        size: "sm",
        variant: reminder ? "secondary" : "primary",
        isDisabled: reminder === void 0,
        onPress: open,
        "aria-label": fullLabel,
        title: fullLabel,
        className: "whitespace-nowrap",
        children: [
          /* @__PURE__ */ jsx("i", { className: reminder ? "ri-alarm-fill" : "ri-alarm-line", "aria-hidden": true }),
          reminder === void 0 ? "Checking…" : label
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      ReminderDrawer,
      {
        allowTeamsChannel,
        isSaving,
        onSave: handleSave,
        onDelete: handleDelete
      }
    )
  ] });
};
const SourceReminder = (props) => /* @__PURE__ */ jsx(Embed, { children: /* @__PURE__ */ jsx(RequirePolicy, { resource: "Reminders", operation: "create", children: /* @__PURE__ */ jsx(SourceReminderController, { ...props }) }) });
const Router = () => /* @__PURE__ */ jsx(AppLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "reminders/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "reminders", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(RemindersRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "reminders", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(RemindersRoutes, {}) })
    }
  )
] }) });
function getRemindersNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: user.hasApiAccess(apiKey),
    items: [
      {
        title: "My Reminders",
        route: "/reminders",
        icon: "ri-calendar-2-line",
        isVisible: user.hasPolicy(apiKey, "reminders", "view")
      }
    ]
  };
}
function getRemindersResources(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  const items = [];
  if (user.hasPolicy(apiKey, "reminders", "create")) {
    items.push({
      key: "reminder",
      label: "New Reminder",
      route: "/reminders",
      description: "Create a new reminder"
    });
  }
  return {
    items,
    handlers: ((navigate) => ({
      reminder: () => navigate(RoutePaths().reminders.create())
    }))
  };
}
let appRuntime = null;
const initRemindersRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("reminders", {
    description: {
      title: "Reminders",
      iconClass: "ri-alarm-fill",
      defaultRoute: "/reminders",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getRemindersNavigation,
    resources: getRemindersResources,
    config: () => RemindersConfig.current
  });
  hostRuntime.components.register("reminders.calendar", RemindersCalendar);
  hostRuntime.components.register(SOURCE_REMINDER, SourceReminder);
};
export {
  initRemindersRuntime
};
//# sourceMappingURL=index.js.map
